import { useEffect, useState } from "react";
import {
  FAMILY_EMAIL_DELAYS,
  formatFamilyEmailWhen,
} from "../utils/familyEmailSchedule";

// One dialog does both jobs: stop a submit that would email the family before
// the pet has passed, and let the vet submit the case now while holding that
// email for 6, 12, or 24 hours. Rows stay visible so the send time is part of
// the choice, not hidden inside a closed dropdown.
export function ConfirmSubmitModal({ data, brand, onCancel, onConfirm }) {
  const [delayHours, setDelayHours] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") {
        onCancel();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onCancel]);

  // Keep "6:45 PM" honest if the dialog stays open across a minute boundary.
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30 * 1000);
    return () => window.clearInterval(id);
  }, []);

  if (!data) return null;

  const petName = data.petName?.trim() || "this pet";
  const brandName = brand?.name || "Evvi";

  return (
    <div className="confirm-overlay" data-cy="confirm-submit-modal">
      <div
        className="confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-submit-heading"
      >
        <h3 id="confirm-submit-heading">Has {petName}'s passed?</h3>

        <label className="confirm-checkbox">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            data-cy="confirm-passed-checkbox"
          />
          <span>Yes, {petName} has passed and the family can be emailed.</span>
        </label>

        <p className="confirm-warning">
          Submitting starts {brandName}&rsquo;s process and the family will be
          emailed at the time you choose. If you are not ready to submit the
          case, go back and save it as a draft.
        </p>

        <fieldset className="confirm-schedule" data-cy="confirm-email-schedule">
          <legend>When should the family be emailed?</legend>
          {FAMILY_EMAIL_DELAYS.map((option) => {
            const selected = delayHours === option.hours;
            return (
              <label
                key={option.hours}
                className={`confirm-schedule-option${
                  selected ? " is-selected" : ""
                }`}
              >
                <span className="confirm-schedule-choice">
                  <input
                    type="radio"
                    name="familyEmailDelay"
                    value={option.hours}
                    checked={selected}
                    onChange={() => setDelayHours(option.hours)}
                    data-cy={`confirm-email-delay-${option.hours}`}
                  />
                  <span>{option.label}</span>
                </span>
                <span className="confirm-schedule-when">
                  {formatFamilyEmailWhen(option.hours, now)}
                </span>
              </label>
            );
          })}
        </fieldset>

        <p className="confirm-notified">
          Family email: <strong>{data.ownerEmail}</strong>
        </p>

        <div className="confirm-actions">
          <button
            type="button"
            className="button button-secondary"
            onClick={onCancel}
            data-cy="confirm-submit-cancel"
          >
            Go back
          </button>
          <button
            type="button"
            className="button"
            onClick={() => onConfirm(delayHours)}
            disabled={!confirmed}
            data-cy="confirm-submit-button"
          >
            Submit case
          </button>
        </div>
      </div>
    </div>
  );
}
